import { CircleHelp, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/** What the Expired VMs page lists and what each button does, in the shape of the other Helper cards. */
export function ExpiredVmsHowThisWorks() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm"
        >
          <CircleHelp className="h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] text-sm">
        <h3 className="mb-2 font-semibold">How this works</h3>
        <ol className="list-decimal space-y-2 pl-4 text-muted-foreground">
          <li>
            <span className="text-foreground">Trial / Paid / Other</span> list
            VMs that are Running, Stopped, Suspended, in Error or being deleted
            (Terminating) and whose term ended at least{" "}
            <span className="text-foreground">N days</span> ago (15 / 30 / 3 by
            default). Trial and Paid: the VM&apos;s own billing cycle ends on
            its expiry, paid with the sign-up bonus or any other way. Other: no
            matching cycle, or no expiry at all — data worth checking.
          </li>
          <li>
            <span className="text-foreground">Disks &amp; networks</span> lists
            disks and networks created at least a day ago that were never
            attached to a VM, or whose VM was deleted and expired at least N
            days ago (counted from the deletion when it had no expiry; 30 by
            default). Filter by N and by whether a row had a VM. Removing
            re-checks each row with the same N, one after another, and leaves
            any row whose former VM still exists at n8n.
          </li>
          <li>
            The <span className="text-foreground">N8n Infrastructure</span>{" "}
            column is read from n8n after the table loads, one request per VM,
            all at once, and shows one status: Running, Stopped or Suspended as
            WiCloud reads n8n&apos;s answer, the lock that holds the VM, or Not
            found when n8n has no machine with its UUID.
          </li>
          <li>
            <span className="text-foreground">Delete n8n only</span> in a row's
            Actions menu deletes the real machine directly by its confirmed
            UUID. WiCloud's record and billing remain unchanged. The dialog
            stays open with loading while n8n answers; use Re-check to verify
            completion.
          </li>
          <li>
            <span className="text-foreground">Delete</span> in a row's menu or{" "}
            <span className="text-foreground">Delete selected</span> runs the
            shared deletion: n8n must have the VM, its expiry and billing-cycle
            end must match, and the selected type and expiry filters must still
            hold. The VM stays in the list as Terminating — locked — until n8n
            removes it; it then leaves the list and stays restorable for 1 hour.
            The page re-checks those rows every 15 seconds and says when one
            finishes.{" "}
            <span className="text-foreground">Started is not deleted</span>: if
            n8n refuses, the VM comes back a few minutes later marked &quot;Last
            deletion failed&quot;. A row that stays Terminating is stuck at n8n;
            its own page&apos;s Actions tab shows what was asked for.
          </li>
          <li>
            <span className="text-foreground">Delete record only</span> removes
            WiCloud&apos;s record and sends nothing to n8n — for a VM n8n no
            longer has, and only while it still matches the tab&apos;s filters.
            A machine still running there keeps running, unbilled.{" "}
            <span className="text-foreground">Remove record</span> on an orphan
            looks up each former VM at n8n first, then marks the orphan
            Terminated and detaches it from its deleted VM; nothing is changed
            at n8n.
          </li>
          <li>
            Up to <span className="text-foreground">10 VMs</span> (50 orphans)
            per request, one VM batch at a time per admin, 5 delete requests and
            100 state reads a minute (one per VM). A VM renewed since the page
            loaded, or with a renewal payment awaiting verification, is refused
            and listed in the report.
          </li>
        </ol>

        <Button variant="outline" size="sm" asChild className="mt-3 w-full">
          <a
            href="/docs/expired-vm-cleanup.html"
            target="_blank"
            rel="noreferrer"
          >
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
